import React, { useContext, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import {
  Box,
  Chip,
  Grid,
  LinearProgress,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import {
  Add,
  CalendarMonth,
  Delete,
  Edit,
  Factory,
  Paid,
  Schedule,
} from '@mui/icons-material';
import { DataGrid, GridActionsCellItem } from '@mui/x-data-grid';
import { DropsContext } from 'contexts/DropsContext';
import { RecursosContext } from 'contexts/RecursosContext';
import { useDialog, useModal } from 'components/Modals';
import { CustomPagination } from 'utils/functions';
import { dropBoolean, dropTipoRecurso, dropTipoCalendario } from 'utils/drops';
import Container from 'components/Container';
import Header from 'components/Header';
import Card from 'components/Card';
import Button from 'components/Button';
import Input from 'components/Input';
import Dropdown from 'components/Dropdown';
import DeleteModal from 'components/Modals/DeleteModal';
import localeText from 'utils/localeText';
import InputMask from 'components/InputMask';

const emptyRecurso = {
  ccusto_id: null,
  codigo: '',
  descricao: '',
  tipo: null,
  eficiencia: 1,
  disponibilidade: 1,
  ativo: 'SIM',
};

const emptyTurno = {
  descricao: '',
  hora_inicio: '',
  hora_fim: '',
  intervalo_inicio: '',
  intervalo_fim: '',
  fator_capacidade: 1,
  ativo: 'SIM',
};

const emptyCalendario = {
  data: null,
  tipo: 'NORMAL',
  horas_disponiveis: '',
  observacao: '',
};

const emptyValor = {
  valor_hora: '',
  inivalid: null,
  fimvalid: null,
};

const currencyFormatter = new Intl.NumberFormat('pt-BR', {
  style: 'currency',
  currency: 'BRL',
});

const percentFormatter = new Intl.NumberFormat('pt-BR', {
  style: 'percent',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const rotinas = {
  recursos: 'CcustoRecurso',
  calendarios: 'CcustoRecursoCalendario',
  turnos: 'CcustoRecursoTurno',
  valores: 'CcustoRecursoValor',
};

const getLabel = (options = [], value) =>
  options?.find((option) => String(option?.value) === String(value))?.label ||
  '';

const parseNumber = (value) =>
  value === '' || value === null || value === undefined ? null : Number(value);

const formatDate = (value) => {
  if (!value) {
    return '';
  }

  const [date] = String(value).split('T');
  const [year, month, day] = date.split('-');

  if (!year || !month || !day) {
    return value;
  }

  return `${day}/${month}/${year}`;
};

const getFirstArray = (source, keys) =>
  keys.map((key) => source?.[key]).find((value) => Array.isArray(value));

const normalizeRecursosPayload = (payload) => {
  const source = payload?.data || payload || {};
  const recursos = Array.isArray(source)
    ? source
    : getFirstArray(source, [
        'recursos',
        'Recursos',
        'CcustoRecurso',
        'CcustoRecursos',
        'ccusto_recurso',
      ]) || [];

  const nested = recursos.reduce(
    (acc, recurso) => {
      const parentId = recurso?.id;
      const turnos = getFirstArray(recurso, [
        'turnos',
        'Turnos',
        'CcustoRecursoTurnos',
        'CcustoRecursoTurno',
      ]);
      const calendarios = getFirstArray(recurso, [
        'calendarios',
        'Calendarios',
        'Calendários',
        'CcustoRecursoCalendarios',
        'CcustoRecursoCalendario',
      ]);
      const valores = getFirstArray(recurso, [
        'valores',
        'Valores',
        'CcustoRecursoValors',
        'CcustoRecursoValores',
        'CcustoRecursoValor',
      ]);

      acc.turnos.push(
        ...(turnos || []).map((item) => ({
          ...item,
          ccusto_recurso_id: item?.ccusto_recurso_id || parentId,
        }))
      );
      acc.calendarios.push(
        ...(calendarios || []).map((item) => ({
          ...item,
          ccusto_recurso_id: item?.ccusto_recurso_id || parentId,
        }))
      );
      acc.valores.push(
        ...(valores || []).map((item) => ({
          ...item,
          ccusto_recurso_id: item?.ccusto_recurso_id || parentId,
        }))
      );

      return acc;
    },
    { turnos: [], calendarios: [], valores: [] }
  );

  return {
    recursos,
    turnos:
      getFirstArray(source, [
        'turnos',
        'Turnos',
        'CcustoRecursoTurnos',
        'CcustoRecursoTurno',
      ]) || nested.turnos,
    calendarios:
      getFirstArray(source, [
        'calendarios',
        'Calendarios',
        'Calendários',
        'CcustoRecursoCalendarios',
        'CcustoRecursoCalendario',
      ]) || nested.calendarios,
    valores:
      getFirstArray(source, [
        'valores',
        'Valores',
        'CcustoRecursoValors',
        'CcustoRecursoValores',
        'CcustoRecursoValor',
      ]) || nested.valores,
  };
};

const RecursoModal = ({ item, onSubmit }) => {
  const { closeDialog } = useDialog();
  const { drops } = useContext(DropsContext);
  const { upsertLoading } = useContext(RecursosContext);
  const { control, handleSubmit } = useForm({
    defaultValues: { ...emptyRecurso, ...item },
  });

  const submit = (values) => {
    onSubmit(
      {
        ...values,
        eficiencia: parseNumber(values?.eficiencia) || 0,
        disponibilidade: parseNumber(values?.disponibilidade) || 0,
      },
      closeDialog
    );
  };

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Input name="descricao" control={control} label="Descrição" />
      </Grid>
      <Grid item xs={12} sm={4}>
        <Input name="codigo" control={control} label="Código" />
      </Grid>
      <Grid item xs={12} sm={8}>
        <Dropdown
          name="ccusto_id"
          control={control}
          options={drops?.Ccusto}
          label="Centro de Custo"
        />
      </Grid>
      <Grid item xs={12} sm={3}>
        <Dropdown
          name="tipo"
          label="Tipo"
          control={control}
          options={dropTipoRecurso}
        />
      </Grid>
      <Grid item xs={12} sm={3}>
        <Dropdown
          name="ativo"
          label="Ativo"
          control={control}
          options={dropBoolean}
        />
      </Grid>
      <Grid item xs={12} sm={3}>
        <Input
          type="number"
          name="eficiencia"
          control={control}
          label="Eficiência"
        />
      </Grid>
      <Grid item xs={12} sm={3}>
        <Input
          type="number"
          control={control}
          name="disponibilidade"
          label="Disponibilidade"
        />
      </Grid>
      <Grid item xs={12} textAlign="center">
        <Button
          variant="contained"
          onClick={handleSubmit(submit)}
          loading={upsertLoading}
        >
          Salvar
        </Button>
      </Grid>
    </Grid>
  );
};

const TurnoModal = ({ item, onSubmit }) => {
  const { closeDialog } = useDialog();
  const { upsertLoading } = useContext(RecursosContext);
  const { control, handleSubmit } = useForm({
    defaultValues: { ...emptyTurno, ...item },
  });

  const submit = (values) => {
    onSubmit(
      {
        ...values,
        fator_capacidade: parseNumber(values?.fator_capacidade) || 0,
      },
      closeDialog
    );
  };

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} sm={6}>
        <Input name="descricao" control={control} label="Descrição" />
      </Grid>
      <Grid item xs={12} sm={3}>
        <Dropdown
          name="ativo"
          label="Ativo"
          control={control}
          options={dropBoolean}
        />
      </Grid>
      <Grid item xs={12} sm={3}>
        <Input
          type="number"
          control={control}
          name="fator_capacidade"
          label="Fator de Capacidade"
        />
      </Grid>
      <Grid item xs={12} sm={3}>
        <InputMask
          type="time"
          label="Hora Início"
          control={control}
          name="hora_inicio"
        />
      </Grid>
      <Grid item xs={12} sm={3}>
        <InputMask
          type="time"
          label="Hora Fim"
          name="hora_fim"
          control={control}
        />
      </Grid>
      <Grid item xs={12} sm={3}>
        <InputMask
          type="time"
          control={control}
          name="intervalo_inicio"
          label="Início Intervalo"
        />
      </Grid>
      <Grid item xs={12} sm={3}>
        <InputMask
          type="time"
          control={control}
          name="intervalo_fim"
          label="Fim Intervalo"
        />
      </Grid>
      <Grid item xs={12} textAlign="center">
        <Button
          variant="contained"
          onClick={handleSubmit(submit)}
          loading={upsertLoading}
        >
          Salvar
        </Button>
      </Grid>
    </Grid>
  );
};

const CalendarioModal = ({ item, onSubmit }) => {
  const { closeDialog } = useDialog();
  const { upsertLoading } = useContext(RecursosContext);
  const { control, handleSubmit } = useForm({
    defaultValues: { ...emptyCalendario, ...item },
  });

  const submit = (values) => {
    onSubmit(
      {
        ...values,
        horas_disponiveis: parseNumber(values?.horas_disponiveis),
      },
      closeDialog
    );
  };

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} sm={4}>
        <InputMask name="data" type="date" label="Data" control={control} />
      </Grid>
      <Grid item xs={12} sm={4}>
        <Dropdown
          name="tipo"
          control={control}
          label="Tipo"
          options={dropTipoCalendario}
        />
      </Grid>
      <Grid item xs={12} sm={4}>
        <Input
          type="number"
          control={control}
          name="horas_disponiveis"
          label="Horas Disponíveis"
        />
      </Grid>
      <Grid item xs={12}>
        <Input
          rows={4}
          multiline
          name="observacao"
          control={control}
          label="Observação"
        />
      </Grid>
      <Grid item xs={12} textAlign="center">
        <Button
          variant="contained"
          onClick={handleSubmit(submit)}
          loading={upsertLoading}
        >
          Salvar
        </Button>
      </Grid>
    </Grid>
  );
};

const ValorModal = ({ item, onSubmit }) => {
  const { closeDialog } = useDialog();
  const { upsertLoading } = useContext(RecursosContext);
  const { control, handleSubmit } = useForm({
    defaultValues: { ...emptyValor, ...item },
  });

  const submit = (values) => {
    onSubmit(
      {
        ...values,
        valor_hora: parseNumber(values?.valor_hora),
      },
      closeDialog
    );
  };

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} sm={4}>
        <InputMask
          type="number"
          name="valor_hora"
          control={control}
          label="Valor / Hora"
        />
      </Grid>
      <Grid item xs={12} sm={4}>
        <InputMask
          type="date"
          name="inivalid"
          control={control}
          label="Início Validade"
        />
      </Grid>
      <Grid item xs={12} sm={4}>
        <InputMask
          type="date"
          name="fimvalid"
          control={control}
          label="Fim Validade"
        />
      </Grid>
      <Grid item xs={12} textAlign="center">
        <Button
          variant="contained"
          onClick={handleSubmit(submit)}
          loading={upsertLoading}
        >
          Salvar
        </Button>
      </Grid>
    </Grid>
  );
};

const Recursos = () => {
  const { drops } = useContext(DropsContext);
  const {
    getLoading,
    getRecursos,
    upsertRecurso,
    deleteLoading,
    deleteRecurso: removeRecurso,
  } = useContext(RecursosContext);
  const { openDialog } = useDialog();
  const { openModal, closeModal } = useModal();
  const [tab, setTab] = useState('turnos');
  const [selectedId, setSelectedId] = useState(null);
  const [recursos, setRecursos] = useState([]);
  const [turnos, setTurnos] = useState([]);
  const [calendarios, setCalendarios] = useState([]);
  const [valores, setValores] = useState([]);

  const loadData = () => {
    getRecursos({
      cb: (data) => {
        const normalized = normalizeRecursosPayload(data);
        setRecursos(normalized.recursos);
        setTurnos(normalized.turnos);
        setCalendarios(normalized.calendarios);
        setValores(normalized.valores);
        setSelectedId((prev) =>
          normalized.recursos.some((item) => item?.id === prev) ? prev : null
        );
      },
    });
  };

  useEffect(() => {
    loadData();
  }, []);

  const selectedRecurso = recursos.find((item) => item?.id === selectedId);

  const childRows = {
    turnos: turnos.filter((item) => item?.ccusto_recurso_id === selectedId),
    calendarios: calendarios.filter(
      (item) => item?.ccusto_recurso_id === selectedId
    ),
    valores: valores.filter((item) => item?.ccusto_recurso_id === selectedId),
  };

  const handleUpsertRecurso = (values, cb) => {
    upsertRecurso({
      rotina: rotinas.recursos,
      data: values,
      cb: () => {
        cb?.();
        loadData();
      },
    });
  };

  const handleDelete = ({ rotina, row }) => {
    removeRecurso({
      rotina,
      id: row?.id,
      cb: () => {
        if (rotina === rotinas.recursos && selectedId === row?.id) {
          setSelectedId(null);
        }

        closeModal();
        loadData();
      },
    });
  };

  const upsertChild = ({ rotina, values, cb }) => {
    if (!selectedId) {
      return;
    }

    upsertRecurso({
      rotina,
      data: {
        ...values,
        ccusto_recurso_id: values?.ccusto_recurso_id || selectedId,
      },
      cb: () => {
        cb?.();
        loadData();
      },
    });
  };

  const confirmDelete = (onSubmit) => {
    openModal(<DeleteModal onSubmit={onSubmit} />);
  };

  const openRecursoModal = (item) =>
    openDialog(
      <RecursoModal item={item} onSubmit={handleUpsertRecurso} />,
      item?.id ? 'Editar Recurso' : 'Adicionar Recurso'
    );

  const openTurnoModal = (item) =>
    openDialog(
      <TurnoModal
        item={item}
        onSubmit={(values, cb) =>
          upsertChild({ rotina: rotinas.turnos, values, cb })
        }
      />,
      item?.id ? 'Editar Turno' : 'Adicionar Turno'
    );

  const openCalendarioModal = (item) =>
    openDialog(
      <CalendarioModal
        item={item}
        onSubmit={(values, cb) =>
          upsertChild({ rotina: rotinas.calendarios, values, cb })
        }
      />,
      item?.id ? 'Editar Calendário' : 'Adicionar Calendário'
    );

  const openValorModal = (item) =>
    openDialog(
      <ValorModal
        item={item}
        onSubmit={(values, cb) =>
          upsertChild({ rotina: rotinas.valores, values, cb })
        }
      />,
      item?.id ? 'Editar Valor' : 'Adicionar Valor'
    );

  const actionColumn = (onEdit, onDelete) => ({
    field: 'actions',
    headerName: 'Ações',
    type: 'actions',
    width: 90,
    getActions: ({ row }) => [
      <GridActionsCellItem
        icon={<Edit />}
        label="Editar"
        onClick={() => onEdit(row)}
      />,
      <GridActionsCellItem
        icon={<Delete />}
        label="Excluir"
        onClick={() => confirmDelete(() => onDelete(row))}
      />,
    ],
  });

  const recursosColumns = [
    {
      field: 'codigo',
      headerName: 'Código',
      width: 120,
    },
    {
      field: 'descricao',
      headerName: 'Recurso',
      minWidth: 220,
      flex: 1,
    },
    {
      field: 'ccusto_id',
      headerName: 'Centro de Custo',
      minWidth: 180,
      flex: 1,
      valueFormatter: ({ value }) => getLabel(drops?.Ccusto, value),
    },
    {
      field: 'tipo',
      headerName: 'Tipo',
      width: 140,
    },
    {
      field: 'horas_dia',
      headerName: 'Horas / Dia',
      type: 'number',
      width: 120,
    },
    {
      field: 'custo_hora',
      headerName: 'Custo / Hora',
      width: 140,
      valueFormatter: ({ value }) =>
        value !== null && value !== undefined
          ? currencyFormatter.format(value)
          : '',
    },
    {
      field: 'eficiencia',
      headerName: 'Eficiência',
      width: 120,
      valueFormatter: ({ value }) =>
        value !== null && value !== undefined
          ? percentFormatter.format(value)
          : '',
    },
    {
      field: 'ativo',
      headerName: 'Ativo',
      width: 100,
      renderCell: ({ value }) => (
        <Chip
          size="small"
          label={value}
          sx={{ width: '100%' }}
          color={value === 'SIM' ? 'success' : 'default'}
          variant={value === 'SIM' ? 'filled' : 'outlined'}
        />
      ),
    },
    actionColumn(openRecursoModal, (row) =>
      handleDelete({ rotina: rotinas.recursos, row })
    ),
  ];

  const turnosColumns = [
    { field: 'descricao', headerName: 'Descrição', minWidth: 220, flex: 1 },
    { field: 'hora_inicio', headerName: 'Hora Início', width: 120 },
    { field: 'hora_fim', headerName: 'Hora Fim', width: 120 },
    { field: 'intervalo_inicio', headerName: 'Início Intervalo', width: 140 },
    { field: 'intervalo_fim', headerName: 'Fim Intervalo', width: 130 },
    {
      field: 'fator_capacidade',
      headerName: 'Fator',
      width: 110,
      type: 'number',
    },
    { field: 'ativo', headerName: 'Ativo', width: 100 },
    actionColumn(openTurnoModal, (row) =>
      handleDelete({ rotina: rotinas.turnos, row })
    ),
  ];

  const calendariosColumns = [
    {
      field: 'data',
      headerName: 'Data',
      width: 130,
      valueFormatter: ({ value }) => formatDate(value),
    },
    { field: 'tipo', headerName: 'Tipo', width: 150 },
    {
      field: 'horas_disponiveis',
      headerName: 'Horas',
      width: 110,
      type: 'number',
    },
    { field: 'observacao', headerName: 'Observação', minWidth: 260, flex: 1 },
    actionColumn(openCalendarioModal, (row) =>
      handleDelete({ rotina: rotinas.calendarios, row })
    ),
  ];

  const valoresColumns = [
    {
      field: 'valor_hora',
      headerName: 'Valor / Hora',
      minWidth: 150,
      flex: 1,
      valueFormatter: ({ value }) =>
        value !== null && value !== undefined
          ? currencyFormatter.format(value)
          : '',
    },
    {
      field: 'inivalid',
      headerName: 'Início Validade',
      minWidth: 150,
      flex: 1,
      valueFormatter: ({ value }) => formatDate(value),
    },
    {
      field: 'fimvalid',
      headerName: 'Fim Validade',
      minWidth: 150,
      flex: 1,
      valueFormatter: ({ value }) => formatDate(value),
    },
    actionColumn(openValorModal, (row) =>
      handleDelete({ rotina: rotinas.valores, row })
    ),
  ];

  const detailConfig = {
    turnos: {
      title: 'Turnos',
      icon: <Schedule fontSize="small" />,
      rows: childRows.turnos,
      columns: turnosColumns,
      onAdd: () => openTurnoModal(),
    },
    calendarios: {
      title: 'Calendário',
      icon: <CalendarMonth fontSize="small" />,
      rows: childRows.calendarios,
      columns: calendariosColumns,
      onAdd: () => openCalendarioModal(),
    },
    valores: {
      title: 'Valores',
      icon: <Paid fontSize="small" />,
      rows: childRows.valores,
      columns: valoresColumns,
      onAdd: () => openValorModal(),
    },
  };

  const selectedDetail = detailConfig[tab];

  return (
    <Container>
      <Header
        titulo="Recursos da Produção"
        subtitulo="Cadastre máquinas, operadores, postos, linhas e os parâmetros de capacidade usados pela produção."
      />
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Card title="Recursos">
            <Grid container spacing={2}>
              <Grid item xs={12} textAlign="center">
                <Button
                  variant="outlined"
                  onClick={() => openRecursoModal()}
                  loading={getLoading || deleteLoading}
                >
                  Adicionar
                </Button>
              </Grid>
              <Grid item xs={12}>
                <DataGrid
                  rows={recursos}
                  columns={recursosColumns}
                  autoHeight
                  density="compact"
                  disableSelectionOnClick
                  disableColumnMenu
                  showCellRightBorder
                  showColumnRightBorder
                  localeText={localeText}
                  pageSize={10}
                  loading={getLoading || deleteLoading}
                  selectionModel={selectedId ? [selectedId] : []}
                  onRowClick={({ row }) => setSelectedId(row?.id)}
                  components={{
                    Pagination: CustomPagination,
                    LoadingOverlay: LinearProgress,
                  }}
                />
              </Grid>
            </Grid>
          </Card>
        </Grid>
        <Grid item xs={12}>
          <Card
            title={
              selectedRecurso
                ? `${selectedRecurso?.codigo} - ${selectedRecurso?.descricao}`
                : 'Detalhes do Recurso'
            }
          >
            <Grid container spacing={2}>
              <Grid item xs={12} display={selectedRecurso ? 'none' : 'block'}>
                <Box
                  p={5}
                  gap={1}
                  display="flex"
                  alignItems="center"
                  flexDirection="column"
                >
                  <Factory color="disabled" fontSize="large" />
                  <Typography color="text.secondary">
                    Selecione um recurso para manter turnos, calendário e
                    valores.
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} display={selectedRecurso ? 'block' : 'none'}>
                <Tabs
                  value={tab}
                  variant="fullWidth"
                  scrollButtons="auto"
                  onChange={(_, value) => setTab(value)}
                >
                  {Object.entries(detailConfig).map(([value, config]) => (
                    <Tab
                      key={value}
                      value={value}
                      icon={config.icon}
                      label={config.title}
                      iconPosition="start"
                    />
                  ))}
                </Tabs>
              </Grid>
              <Grid
                item
                xs={12}
                textAlign="center"
                display={selectedRecurso ? 'block' : 'none'}
              >
                <Button
                  variant="contained"
                  startIcon={<Add />}
                  onClick={selectedDetail.onAdd}
                >
                  Adicionar
                </Button>
              </Grid>
              <Grid item xs={12} display={selectedRecurso ? 'block' : 'none'}>
                <DataGrid
                  rows={selectedDetail.rows}
                  columns={selectedDetail.columns}
                  autoHeight
                  density="compact"
                  disableSelectionOnClick
                  disableColumnMenu
                  showCellRightBorder
                  showColumnRightBorder
                  localeText={localeText}
                  loading={getLoading || deleteLoading}
                  components={{
                    Pagination: CustomPagination,
                    LoadingOverlay: LinearProgress,
                  }}
                />
              </Grid>
            </Grid>
          </Card>
        </Grid>
      </Grid>
    </Container>
  );
};

export default Recursos;
